import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

type Tone = "error" | "warning" | "success" | "info";

const TONES: Record<Tone, { icon: typeof Info; classes: string }> = {
  error: { icon: XCircle, classes: "border-error bg-error-soft text-error" },
  warning: { icon: AlertTriangle, classes: "border-warning bg-warning-soft text-warning" },
  success: { icon: CheckCircle2, classes: "border-success bg-success-soft text-success" },
  info: { icon: Info, classes: "border-info bg-info-soft text-info" },
};

/**
 * A message box. The tone always shows as an icon as well as a colour
 * (WCAG 1.4.1), and errors are announced to screen readers.
 */
export function Alert({
  tone,
  title,
  children,
}: {
  tone: Tone;
  title?: string;
  children?: ReactNode;
}) {
  const { icon: Icon, classes } = TONES[tone];
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cn("flex gap-3 rounded-md border-l-4 p-4", classes)}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
      <div className="text-foreground">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className="text-sm">{children}</div> : null}
      </div>
    </div>
  );
}
